import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    addDoc, collection, doc, getDoc, onSnapshot, orderBy, query, serverTimestamp, updateDoc,
} from 'firebase/firestore';
import {
    ArrowLeft, Copy, MessageCircle, Globe, Facebook, StickyNote, Pencil, Check, X, Send, Menu,
} from 'lucide-react';
import { auth, db } from './firebase';
import DashboardNav from './DashboardNav';
import {
    CRM_CONFIGS, getInfo, DEFAULT_STATUS, StatusDropdown, Toast, useToast,
    copyToClipboard, whatsAppLink, getWebsiteLink, formatDate, formatDateTime, initials,
} from './resellerCrmShared';

const inputCls = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:border-orange-400';
const linkBtn = 'inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 transition';

export default function ResellerDetailPage({ config = CRM_CONFIGS.reseller }) {
    const cfg = config;
    const { id } = useParams();
    const navigate = useNavigate();
    const { toast, showToast } = useToast();
    const [sidebarOpen, setSidebarOpen] = useState(() => typeof window === 'undefined' || window.innerWidth >= 1024);

    const [reseller, setReseller] = useState(null);
    const [loading, setLoading] = useState(true);

    // Notes (live)
    const [notes, setNotes] = useState([]);
    const [notesLoading, setNotesLoading] = useState(true);
    const [noteText, setNoteText] = useState('');
    const [noteSaving, setNoteSaving] = useState(false);

    // Info edit
    const [editingInfo, setEditingInfo] = useState(false);
    const [savingInfo, setSavingInfo] = useState(false);
    const [infoForm, setInfoForm] = useState({ email: '', address: '', facebook_link: '', website_link: '' });

    useEffect(() => {
        let active = true;
        setLoading(true);
        getDoc(doc(db, cfg.collection, id))
            .then((snap) => { if (active) setReseller(snap.exists() ? { id: snap.id, ...snap.data() } : null); })
            .catch((e) => { console.error('Load reseller failed:', e?.code, e?.message); if (active) setReseller(null); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [id, cfg.collection]);

    useEffect(() => {
        setNotesLoading(true);
        const q = query(collection(db, cfg.collection, String(id), 'notes'), orderBy('created_at', 'desc'));
        return onSnapshot(
            q,
            (snap) => { setNotes(snap.docs.map((d) => ({ id: d.id, ...d.data() }))); setNotesLoading(false); },
            () => setNotesLoading(false),
        );
    }, [id, cfg.collection]);

    const handleStatusChange = async (newStatus) => {
        setReseller((prev) => ({ ...prev, crm_status: newStatus }));
        try {
            await updateDoc(doc(db, cfg.collection, id), { crm_status: newStatus });
            showToast('Status updated');
        } catch (err) {
            console.error('updateDoc failed:', err?.code, err?.message);
            showToast('Could not update status', 'error');
        }
    };

    const handleCopy = async (phone) => {
        const ok = await copyToClipboard(phone);
        showToast(ok ? 'Number copied' : 'Could not copy number', ok ? 'success' : 'error');
    };

    const startEditingInfo = () => {
        const cur = getInfo(cfg, reseller);
        setInfoForm({
            email: cur.email || '',
            address: cur.address || '',
            facebook_link: cur.facebook_link || '',
            website_link: cur.website_link || '',
        });
        setEditingInfo(true);
    };

    const saveInfo = async () => {
        setSavingInfo(true);
        try {
            await updateDoc(doc(db, cfg.collection, id), {
                [`${cfg.infoKey}.email`]: infoForm.email || null,
                [`${cfg.infoKey}.address`]: infoForm.address || null,
                [`${cfg.infoKey}.facebook_link`]: infoForm.facebook_link || null,
                [`${cfg.infoKey}.website_link`]: infoForm.website_link || null,
            });
            setReseller((prev) => ({ ...prev, [cfg.infoKey]: { ...getInfo(cfg, prev), ...infoForm } }));
            showToast('Info updated');
            setEditingInfo(false);
        } catch (err) {
            console.error('updateDoc failed:', err?.code, err?.message);
            showToast('Could not update info', 'error');
        } finally {
            setSavingInfo(false);
        }
    };

    const addNote = async (e) => {
        e.preventDefault();
        if (!noteText.trim()) return;
        setNoteSaving(true);
        try {
            await addDoc(collection(db, cfg.collection, String(id), 'notes'), {
                text: noteText.trim(),
                created_at: serverTimestamp(),
                created_by: auth.currentUser?.email || 'unknown',
            });
            setNoteText('');
            showToast('Note added');
        } catch (err) {
            console.error('addNote failed:', err?.code, err?.message);
            showToast('Could not add note', 'error');
        } finally {
            setNoteSaving(false);
        }
    };

    const info = getInfo(cfg, reseller);
    const pkg = reseller?.get_package || {};
    const bill = reseller?.subscription_bills || {};
    const phone = info.phone || '';
    const website = getWebsiteLink(info);

    const setField = (key) => (e) => setInfoForm((f) => ({ ...f, [key]: e.target.value }));

    return (
        <div className="flex min-h-screen bg-gray-50 text-gray-900">
            <Toast toast={toast} />
            <DashboardNav sidebarOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

            <div className="flex-1 min-w-0 overflow-auto">
                {/* Header */}
                <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 sticky top-0 z-20">
                    <div className="flex items-center gap-4">
                        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-orange-50 rounded-lg transition">
                            <Menu size={22} />
                        </button>
                        <button onClick={() => navigate(cfg.listPath)}
                            className="inline-flex items-center gap-1 text-sm font-semibold text-gray-500 hover:text-orange-600">
                            <ArrowLeft size={16} /> Back to {cfg.title}
                        </button>
                    </div>
                </div>

                <div className="p-6 max-w-3xl mx-auto space-y-6">
                    {loading && <div className="h-40 animate-pulse rounded-xl bg-gray-100" />}

                    {!loading && !reseller && (
                        <div className="py-10 text-center text-gray-400">
                            {cfg.singular} not found.
                            <div className="mt-4">
                                <button onClick={() => navigate(cfg.listPath)} className="text-orange-600 underline">Back to list</button>
                            </div>
                        </div>
                    )}

                    {!loading && reseller && (
                        <>
                            {/* Profile card */}
                            <div className="rounded-xl border border-gray-200 bg-white p-5">
                                <div className="flex flex-wrap items-start justify-between gap-4">
                                    <div className="flex items-center gap-3">
                                        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-orange-400 text-lg font-semibold text-white">
                                            {initials(info.name) || '—'}
                                        </div>
                                        <div>
                                            <h1 className="text-lg font-bold text-gray-900">{info.name || 'Unnamed'}</h1>
                                            <p className="text-sm text-gray-500">{info.company_name || '—'}</p>
                                        </div>
                                    </div>
                                    <StatusDropdown status={reseller.crm_status || DEFAULT_STATUS} onChange={handleStatusChange} />
                                </div>

                                <div className="mt-5 flex flex-wrap items-center gap-2">
                                    <span className="rounded-lg bg-gray-50 px-3 py-1.5 text-sm text-gray-600">{phone || 'No phone'}</span>
                                    <button type="button" onClick={() => handleCopy(phone)} className={`${linkBtn} hover:bg-gray-50`}>
                                        <Copy size={14} /> Copy
                                    </button>
                                    <a href={whatsAppLink(phone)} target="_blank" rel="noreferrer" className={`${linkBtn} hover:bg-emerald-50 hover:text-emerald-600`}>
                                        <MessageCircle size={14} /> WhatsApp
                                    </a>
                                    {website && (
                                        <a href={website} target="_blank" rel="noreferrer" className={`${linkBtn} hover:bg-blue-50 hover:text-blue-600`}>
                                            <Globe size={14} /> Website
                                        </a>
                                    )}
                                    {info.facebook_link && (
                                        <a href={info.facebook_link} target="_blank" rel="noreferrer" className={`${linkBtn} hover:bg-indigo-50 hover:text-indigo-600`}>
                                            <Facebook size={14} /> Facebook
                                        </a>
                                    )}
                                </div>

                                <div className="mt-5 border-t border-gray-100 pt-4">
                                    <div className="mb-3 flex items-center justify-between">
                                        <span className="text-sm font-medium text-gray-500">Info</span>
                                        {!editingInfo && (
                                            <button type="button" onClick={startEditingInfo}
                                                className="inline-flex items-center gap-1 text-sm font-medium text-orange-600 hover:text-orange-700">
                                                <Pencil size={13} /> Edit
                                            </button>
                                        )}
                                    </div>

                                    {!editingInfo ? (
                                        <dl className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-3">
                                            <div><dt className="text-gray-400">Email</dt><dd className="truncate">{info.email || '—'}</dd></div>
                                            <div><dt className="text-gray-400">Address</dt><dd>{info.address || '—'}</dd></div>
                                            <div><dt className="text-gray-400">Username</dt><dd>{info.username || '—'}</dd></div>
                                            <div><dt className="text-gray-400">Package</dt><dd>{pkg.name || '—'} {pkg.price ? `(৳${pkg.price})` : ''}</dd></div>
                                            <div><dt className="text-gray-400">Subscription ends</dt><dd>{formatDate(reseller.end_date)}</dd></div>
                                            <div><dt className="text-gray-400">Last invoice</dt><dd>{bill.invoice || '—'} {bill.amount ? `· ৳${bill.amount}` : ''}</dd></div>
                                            <div><dt className="text-gray-400">Facebook link</dt><dd className="truncate">{info.facebook_link || '—'}</dd></div>
                                            <div><dt className="text-gray-400">Website link</dt><dd className="truncate">{info.website_link || '—'}</dd></div>
                                        </dl>
                                    ) : (
                                        <div className="space-y-3">
                                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                                <label className="block"><span className="mb-1 block text-xs font-medium text-gray-500">Email</span>
                                                    <input value={infoForm.email} onChange={setField('email')} className={inputCls} /></label>
                                                <label className="block"><span className="mb-1 block text-xs font-medium text-gray-500">Address</span>
                                                    <input value={infoForm.address} onChange={setField('address')} className={inputCls} /></label>
                                                <label className="block"><span className="mb-1 block text-xs font-medium text-gray-500">Facebook page link</span>
                                                    <input value={infoForm.facebook_link} onChange={setField('facebook_link')} placeholder="https://facebook.com/..." className={inputCls} /></label>
                                                <label className="block"><span className="mb-1 block text-xs font-medium text-gray-500">Website link</span>
                                                    <input value={infoForm.website_link} onChange={setField('website_link')}
                                                        placeholder="Leave empty to auto-use mohasagor.com.bd/shop/{username}" className={inputCls} /></label>
                                            </div>
                                            <div className="flex justify-end gap-2">
                                                <button type="button" onClick={() => setEditingInfo(false)}
                                                    className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50">
                                                    <X size={14} /> Cancel
                                                </button>
                                                <button type="button" onClick={saveInfo} disabled={savingInfo}
                                                    className="inline-flex items-center gap-1 rounded-lg bg-orange-400 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-500 disabled:opacity-60">
                                                    <Check size={14} /> {savingInfo ? 'Saving…' : 'Save'}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Notes */}
                            <div className="rounded-xl border border-gray-200 bg-white p-5">
                                <div className="mb-4 flex items-center gap-2">
                                    <StickyNote size={16} className="text-gray-400" />
                                    <h2 className="font-semibold text-gray-900">Notes</h2>
                                </div>

                                <form onSubmit={addNote} className="flex items-start gap-2">
                                    <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={2}
                                        placeholder={`Write a note about this ${cfg.singular.toLowerCase()}…`}
                                        className="flex-1 resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:border-orange-400" />
                                    <button type="submit" disabled={noteSaving || !noteText.trim()}
                                        className="inline-flex h-10 items-center gap-1.5 rounded-lg bg-orange-400 px-3 text-sm font-medium text-white transition hover:bg-orange-500 disabled:cursor-not-allowed disabled:opacity-40">
                                        <Send size={15} /> Add
                                    </button>
                                </form>

                                <div className="mt-4 space-y-2">
                                    {notesLoading && <div className="h-12 animate-pulse rounded-lg bg-gray-100" />}
                                    {!notesLoading && notes.length === 0 && (
                                        <p className="py-6 text-center text-sm text-gray-400">No notes yet. Add the first one above.</p>
                                    )}
                                    {!notesLoading && notes.map((note) => (
                                        <div key={note.id} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                                            <p className="whitespace-pre-wrap text-sm text-gray-900">{note.text}</p>
                                            <div className="mt-1.5 flex items-center gap-2 text-xs text-gray-400">
                                                <span>{note.created_by}</span>
                                                <span aria-hidden="true">·</span>
                                                <span>{formatDateTime(note.created_at)}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
